import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { experienciasApi } from '../api/experiencias'
import Boton from '../components/Boton'
import MenuExperienciaModal from '../components/MenuExperienciaModal'
import { colors, radius, space, sx, text } from '../design/tokens'
import { useStoredState } from '../hooks/useStoredState'
import { fmtFecha, fmtFechaHora, fmtMonto, hoyISO } from '../utils/fecha'

/**
 * EXPERIENCIAS de la tienda web — el listado.
 *
 * Cada fila es una cena, un evento o un viaje: el contenido con el que se
 * publica MÁS el artículo con el que se vende y se factura. El alta nace acá
 * (antes nacía en Magento y Presea se enteraba al final).
 *
 * Duplicar ya no vive acá (Martin, 3 sep 2026): se duplica desde "+ Nueva
 * experiencia" → "Duplicar de", que solo ofrece las de plantilla nueva.
 * "Archivar" guarda sin borrar: la
 * cena pasada sale de la web y del listado, pero el contenido y el artículo
 * quedan (Martin, 2 sep 2026).
 */

/** En qué momento de su vida está la publicación. */
function estadoDe(exp) {
  const hoy = hoyISO()
  if (exp.archivada) return { clave: 'archivada', label: 'Archivada', bg: colors.borderSoft, color: colors.textMeta }
  if (!exp.activo) return { clave: 'guardada', label: 'Guardada', bg: colors.borderSoft, color: colors.textSec }
  if (exp.publicar_hasta && exp.publicar_hasta < hoy) return { clave: 'terminada', label: 'Terminada', bg: colors.borderSoft, color: colors.textMeta }
  if (exp.publicar_desde && exp.publicar_desde > hoy) return { clave: 'programada', label: 'Programada', bg: colors.infoBg, color: colors.info }
  // Oculta = vive, pero no se lista: se vende solo con el link directo.
  if (exp.oculta) return { clave: 'link', label: 'Solo por link', bg: colors.infoBg, color: colors.info }
  return { clave: 'publicada', label: 'Publicada', bg: colors.okBg, color: colors.ok }
}

const ESTADOS_FILTRO = [
  { clave: 'publicada', label: 'Publicadas' },
  { clave: 'programada', label: 'Programadas' },
  { clave: 'link', label: 'Solo por link' },
  { clave: 'guardada', label: 'Guardadas' },
  { clave: 'terminada', label: 'Terminadas' },
  { clave: 'archivada', label: '🗄 Archivadas' },
]

const FILTROS_DEFAULT = { estado: 'todas', seccion: 'todas' }

/** "en 22 días" / "mañana" / "hoy" — solo para fechas por venir (≤ 90 días). */
function enCuanto(iso) {
  // La API manda fecha con hora ("2026-10-20T00:00:00Z"): quedarse con el día.
  const dia = String(iso).slice(0, 10)
  const dias = Math.round((new Date(`${dia}T12:00:00`) - new Date(`${hoyISO()}T12:00:00`)) / 86400000)
  if (dias === 0) return 'hoy'
  if (dias === 1) return 'mañana'
  if (dias > 1 && dias <= 90) return `en ${dias} días`
  return null
}

/** Chip de filtro (mismo lenguaje que la bandeja de WhatsApp). */
function Chip({ activo, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: text.sec, lineHeight: '18px', padding: '3px 10px',
        borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
        border: `1px solid ${activo ? colors.text : colors.border}`,
        background: activo ? colors.text : colors.bg,
        color: activo ? colors.bg : colors.textSec,
        fontWeight: activo ? 600 : 400,
      }}
    >
      {children}
    </button>
  )
}

export default function ExperienciasWebPage() {
  const navigate = useNavigate()
  const [datos, setDatos] = useState({ experiencias: [], tipos: [] })
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [filtros, setFiltros] = useStoredState('lgr.filtros.experiencias', FILTROS_DEFAULT)
  // Menú impreso de la cena (Martin, 10 oct 2026: el botón va acá, en el listado).
  const [menuDe, setMenuDe] = useState(null)

  const cargar = useCallback(() => {
    setCargando(true)
    setError('')
    experienciasApi.listar()
      .then(({ data }) => setDatos(data))
      .catch(() => setError('No se pudieron cargar las experiencias.'))
      .finally(() => setCargando(false))
  }, [])
  useEffect(() => { cargar() }, [cargar])

  const archivar = async (exp) => {
    try {
      await (exp.archivada ? experienciasApi.desarchivar(exp.id) : experienciasApi.archivar(exp.id))
      cargar()
    } catch {
      setError(exp.archivada ? 'No se pudo desarchivar.' : 'No se pudo archivar.')
    }
  }

  // Mail interno con toda la data del evento para que carguen el artículo en
  // Presea (el alta allá sigue siendo manual; esto es el aviso).
  const avisoAlta = async (exp) => {
    try {
      const { data } = await experienciasApi.avisoAlta(exp.id)
      window.alert(`Aviso enviado a ${data.para}${data.cc?.length ? ` (cc: ${data.cc.join(', ')})` : ''}.`)
      cargar()
    } catch {
      setError('No se pudo enviar el aviso de alta.')
    }
  }

  const nombreTipo = (slug) => datos.tipos.find((t) => t.slug === slug)?.nombre || slug
  const pendientes = datos.experiencias.filter((e) => e.presea_pendiente).length

  // "Todas" NO incluye archivadas — para verlas está su chip (mismo criterio
  // que las NV archivadas: fuera de la vista por defecto).
  const visibles = datos.experiencias.filter((exp) => {
    const clave = estadoDe(exp).clave
    const pasaEstado = filtros.estado === 'todas' ? clave !== 'archivada' : clave === filtros.estado
    const pasaSeccion = filtros.seccion === 'todas' || exp.tipo === filtros.seccion
    return pasaEstado && pasaSeccion
  })

  const contarEstado = (clave) => datos.experiencias.filter((e) => estadoDe(e).clave === clave
    && (filtros.seccion === 'todas' || e.tipo === filtros.seccion)).length

  return (
    <div style={{ maxWidth: 1180 }}>
      <div style={sx.toolbar}>
        <h2 style={{ margin: 0, fontSize: text.title, color: colors.text }}>Experiencias</h2>
        <span style={{ fontSize: text.sec, color: colors.textSec }}>
          {visibles.length} en vista
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <Boton variante="primary" onClick={() => navigate('/web/experiencias/nueva')}>
            + Nueva experiencia
          </Boton>
        </div>
      </div>

      <p style={{ margin: `0 0 ${space.lg}px`, fontSize: text.body, color: colors.textSec, maxWidth: '80ch' }}>
        Las cenas, eventos y viajes que se publican en la tienda. Al guardar se crea el
        artículo con el que se vende y se factura; el código de Presea se completa
        cuando el evento se da de alta allá.
      </p>

      <div style={estilos.filtros}>
        <Chip activo={filtros.estado === 'todas'} onClick={() => setFiltros({ ...filtros, estado: 'todas' })}>
          Todas
        </Chip>
        {ESTADOS_FILTRO.map(({ clave, label }) => {
          const n = contarEstado(clave)
          return (
            <Chip key={clave} activo={filtros.estado === clave} onClick={() => setFiltros({ ...filtros, estado: clave })}>
              {label}{n > 0 ? ` ${n}` : ''}
            </Chip>
          )
        })}
        <span style={estilos.separador} />
        <Chip activo={filtros.seccion === 'todas'} onClick={() => setFiltros({ ...filtros, seccion: 'todas' })}>
          Todas las secciones
        </Chip>
        {datos.tipos.map((t) => (
          <Chip key={t.slug} activo={filtros.seccion === t.slug} onClick={() => setFiltros({ ...filtros, seccion: t.slug })}>
            {t.nombre}
          </Chip>
        ))}
      </div>

      {error && <p style={estilos.error}>{error}</p>}

      {pendientes > 0 && (
        <p style={estilos.aviso}>
          {pendientes === 1
            ? 'Una experiencia todavía no tiene código de Presea: se vende, pero la factura va a frenar hasta que lo tenga.'
            : `${pendientes} experiencias todavía no tienen código de Presea: se venden, pero la factura va a frenar hasta que lo tengan.`}
        </p>
      )}

      {cargando ? (
        <p style={{ color: colors.textMeta }}>Cargando…</p>
      ) : visibles.length === 0 ? (
        <p style={estilos.vacio}>
          {datos.experiencias.length === 0
            ? 'Todavía no hay ninguna cargada. Empezá con “+ Nueva experiencia”; después, la siguiente sale de duplicar esta.'
            : 'Nada en esta vista — probá con otro filtro.'}
        </p>
      ) : (
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.lg, overflow: 'hidden' }}>
          <table style={sx.table}>
            <thead>
              <tr>
                <th style={sx.th}>Estado</th>
                <th style={sx.th}>Sección</th>
                <th style={sx.th}>Experiencia</th>
                <th style={sx.th}>Cuándo</th>
                <th style={{ ...sx.th, textAlign: 'right' }}>Precio</th>
                <th style={sx.th}>Artículo</th>
                <th style={sx.th}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((exp) => {
                const estado = estadoDe(exp)
                return (
                  <tr
                    key={exp.id}
                    onClick={() => navigate(`/web/experiencias/${exp.id}`)}
                    title="Ver y editar la experiencia"
                    style={{ borderTop: `1px solid ${colors.borderSoft}`, cursor: 'pointer', height: 52 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgSoft }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '' }}
                  >
                    <td style={sx.td}>
                      <span style={{ ...sx.badge, background: estado.bg, color: estado.color }}>
                        {estado.label}
                      </span>
                    </td>
                    <td style={{ ...sx.td, color: colors.textSec }}>{nombreTipo(exp.tipo)}</td>
                    <td style={sx.td}>
                      <div style={{ fontWeight: 500 }}>{exp.titulo}</div>
                      {exp.lugar && (
                        <div style={{ fontSize: text.sec, color: colors.textMeta }}>{exp.lugar}</div>
                      )}
                    </td>
                    <td style={{ ...sx.td, ...sx.mono, color: colors.textSec }}>
                      {exp.fecha_evento ? (
                        <>
                          <div>{fmtFecha(exp.fecha_evento)}</div>
                          {enCuanto(exp.fecha_evento) && (
                            <div style={{ fontSize: text.sec, color: colors.textMeta, fontFamily: 'inherit' }}>
                              {enCuanto(exp.fecha_evento)}
                            </div>
                          )}
                        </>
                      ) : <span style={sx.placeholder}>sin fecha</span>}
                    </td>
                    <td style={{ ...sx.td, ...sx.mono, textAlign: 'right', fontWeight: 500 }}>
                      {exp.precio != null
                        ? `$ ${fmtMonto(exp.precio, exp.precio % 1 ? 2 : 0)}`
                        : (exp.precio_texto || <span style={sx.placeholder}>—</span>)}
                    </td>
                    <td style={sx.td}>
                      {exp.articulo ? (
                        <>
                          <div style={{ ...sx.mono, fontSize: text.sec }}>{exp.articulo.codigo_alfa}</div>
                          {/* Hermanado: el código con el que Presea lo factura (EVT…). */}
                          {exp.articulo.hermano && (
                            <div style={{ ...sx.mono, fontSize: text.sec, color: colors.textSec }} title={`Se factura como ${exp.articulo.hermano.descripcion}`}>
                              ↔ {exp.articulo.hermano.codigo_alfa}
                            </div>
                          )}
                          {exp.presea_pendiente && (
                            <span style={{ ...sx.badge, background: colors.warnBg, color: colors.warn }}>
                              Sin Presea
                            </span>
                          )}
                        </>
                      ) : (
                        <span style={sx.placeholder}>—</span>
                      )}
                    </td>
                    <td style={{ ...sx.td, whiteSpace: 'nowrap', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      {/* El aviso de alta solo cuando falta el código: con Presea
                          al día no hay nada que avisar. Ya avisado → chip gris
                          (tarea hecha) + ↻ para volver a avisar. */}
                      {exp.articulo && exp.presea_pendiente && (
                        <>
                          {exp.aviso_alta_at ? (
                            <>
                              <span
                                style={{ ...sx.badge, background: colors.bgSoft, color: colors.textSec }}
                                title={`Aviso mandado el ${fmtFechaHora(exp.aviso_alta_at)}`}
                              >
                                📣 Avisado
                              </span>
                              <span style={{ display: 'inline-block', width: space.sm }} />
                              <Boton variante="ghost" sm onClick={() => avisoAlta(exp)} title="Volver a avisar (manda el mail de nuevo)">
                                ↻
                              </Boton>
                            </>
                          ) : (
                            <Boton variante="send" sm onClick={() => avisoAlta(exp)} title="Mandar por mail los datos del evento para el alta en Presea">
                              📣 Aviso de alta
                            </Boton>
                          )}
                          <span style={{ display: 'inline-block', width: space.sm }} />
                        </>
                      )}
                      <Boton variante="secondary" sm onClick={() => setMenuDe(exp)} title="Armar el menú impreso de la cena (PDF horizontal o vertical, con el QR de los vinos)">
                        Generar menú
                      </Boton>
                      <span style={{ display: 'inline-block', width: space.sm }} />
                      <Boton
                        variante="ghost" sm onClick={() => archivar(exp)}
                        title={exp.archivada ? 'Desarchivar: vuelve al listado y a la web' : 'Archivar: sale de la web sin borrar nada'}
                      >
                        {exp.archivada ? '↩' : '🗄'}
                      </Boton>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {menuDe && <MenuExperienciaModal experiencia={menuDe} onClose={() => setMenuDe(null)} />}
    </div>
  )
}

const estilos = {
  filtros: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.sm,
    marginBottom: space.lg,
  },
  separador: {
    width: 1, alignSelf: 'stretch', background: colors.border,
    margin: `0 ${space.sm}px`,
  },
  error: {
    background: colors.errBg, color: colors.err, padding: space.lg,
    borderRadius: radius.sm, fontSize: text.body, marginBottom: space.lg,
  },
  aviso: {
    background: colors.warnBg, color: colors.warn, padding: space.lg,
    borderRadius: radius.sm, fontSize: text.body, marginBottom: space.lg,
  },
  vacio: {
    padding: `${space['3xl']}px`, textAlign: 'center', color: colors.textSec,
    fontSize: text.body, border: `1px dashed ${colors.border}`, borderRadius: radius.lg,
  },
}
